import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Siren, AlertTriangle, Clock, Building2, ListChecks, ChevronRight } from "lucide-react";

export default function Evacuation() {
  const nav = useNavigate();
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteIds, setSiteIds] = useState([]);
  const [reason, setReason] = useState("");
  const [isDrill, setIsDrill] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    try {
      const [a, ev, ss] = await Promise.all([
        api.get("/evacuation/active"),
        api.get("/evacuation/events", { params: { limit: 5 } }),
        api.get("/sites"),
      ]);
      setActive(a.data?.active ? a.data : null);
      setHistory(ev.data || []);
      setSites(ss.data || []);
    } catch (e) { console.warn("[Evacuation] load failed (likely RBAC)", e); }
  };
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/evacuation/start", {
        site_ids: siteIds.length > 0 ? siteIds : null,
        reason: reason || null,
        is_drill: isDrill,
      });
      toast.success(isDrill ? "Drill started" : "Evacuation started");
      nav("/evacuation/active");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="mb-5">
        <div className="eb-overline mb-2">Module 10 · Fire register</div>
        <h1 className="text-3xl font-semibold flex items-center gap-3" data-testid="evac-heading">
          <Siren className="w-7 h-7 text-red-600" />
          Evacuation
        </h1>
        <p className="text-sm text-navy-500 mt-1">
          One-click evacuation, mobile fire register, warden share-link, post-incident report.
        </p>
      </div>

      {active ? (
        <div className="rounded-xl border-2 border-red-500 bg-red-50 p-5 mb-6" data-testid="evac-active-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-7 h-7 text-red-600 flex-shrink-0 animate-pulse" />
            <div className="flex-1">
              <div className="text-lg font-bold text-red-700">EVACUATION ACTIVE</div>
              <div className="text-sm text-red-700">
                Started {active.event?.started_at ? new Date(active.event.started_at).toLocaleString() : "—"}
                {" · "}
                {active.stats.accounted_for}/{active.stats.total} accounted for
              </div>
              <Link to="/evacuation/active" className="btn-accent mt-3" data-testid="go-active">
                Go to active list
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="eb-card p-6 mb-6" data-testid="evac-start-card">
          <h2 className="text-lg font-semibold mb-3">Start evacuation</h2>
          <p className="text-sm text-navy-500 mb-4">
            Sites: <strong>{siteIds.length === 0 ? `all (${sites.length})` : `${siteIds.length} selected`}</strong>
          </p>
          <div className="space-y-2 mb-4">
            {sites.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={siteIds.includes(s.id)}
                  onChange={(e) => setSiteIds((cur) => e.target.checked ? [...cur, s.id] : cur.filter((x) => x !== s.id))}
                  data-testid={`evac-site-${s.id}`}
                />
                <Building2 className="w-3.5 h-3.5 text-navy-400" />
                {s.name}
              </label>
            ))}
          </div>
          <label className="eb-label">Reason (optional)</label>
          <input className="eb-input mb-3"
            placeholder="e.g. fire alarm activated, drill"
            value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="evac-reason"
          />
          <label className="flex items-center gap-2 mb-4 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={isDrill} onChange={(e) => setIsDrill(e.target.checked)}
                   data-testid="evac-is-drill" className="w-4 h-4" />
            <span className="font-medium text-warning-700">Drill mode</span>
            <span className="text-xs text-navy-500">— will be tagged as a drill in the audit log; banner styled differently for staff.</span>
          </label>
          <button onClick={() => setConfirming(true)}
            className={`${isDrill ? "bg-warning-600 hover:bg-warning-700" : "bg-red-600 hover:bg-red-700"} text-white font-bold rounded-lg px-6 py-3 inline-flex items-center gap-2 text-base`}
            data-testid="evac-start-btn">
            <Siren className="w-5 h-5" />
            {isDrill ? "Start drill" : "Start evacuation"}
          </button>
          {confirming && (
            <div className="fixed inset-0 z-50 bg-navy-900/60 flex items-center justify-center p-4" onClick={() => setConfirming(false)}>
              <div className="bg-white rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-red-600 mb-2 inline-flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Start evacuation?
                </h3>
                <p className="text-sm text-navy-700 mb-4">
                  This will lock the live dashboard into the evacuation view and create a fire register
                  for everyone currently signed in across the selected sites. Use only for real
                  evacuations or planned drills.
                </p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setConfirming(false)} className="btn-ghost">Cancel</button>
                  <button onClick={start} disabled={busy}
                    className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg px-4 py-2"
                    data-testid="evac-confirm-start">
                    {busy ? "Starting…" : "Yes — start"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="eb-card p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-navy-100 flex items-center justify-between">
          <h3 className="font-semibold inline-flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-navy-500" /> Recent events
          </h3>
          <Link to="/evacuation/history" className="text-xs text-orange-600 hover:underline" data-testid="evac-history-link">
            See all →
          </Link>
        </div>
        {history.length === 0 ? (
          <p className="py-6 text-center text-sm text-navy-400">No prior events.</p>
        ) : (
          <ul className="divide-y divide-navy-100">
            {history.map((e) => (
              <li key={e.id}>
                <Link to={`/evacuation/history/${e.id}`}
                  className="px-5 py-3 flex items-center gap-3 hover:bg-navy-50/40"
                  data-testid={`evac-history-row-${e.id}`}>
                  <div className={`w-2 h-2 rounded-full ${e.status === "active" ? "bg-red-500 animate-pulse" : "bg-navy-300"}`}></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">
                      {e.status === "active" ? "ACTIVE" : "Ended"} ·
                      {" "}{e.started_at ? new Date(e.started_at).toLocaleString() : ""}
                    </div>
                    <div className="text-xs text-navy-500">
                      {e.site_ids?.length || 0} sites
                      {e.missing_count != null ? ` · ${e.missing_count} missing` : ""}
                      {e.duration_seconds ? ` · ${Math.round(e.duration_seconds / 60)}m duration` : ""}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-navy-300" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
